/**
 * Descifra el campo `definition` que Strapi envía cifrado con AES-256-CBC.
 * Formato del texto cifrado: "<ivBase64>.<ciphertextBase64>"
 *
 * La clave compartida con Strapi se lee de REACT_APP_DEFINITION_CIPHER_KEY (hex, 64 chars = 32 bytes).
 * Nunca se almacena el texto plano en el estado de React — se usa directamente en canvas.
 */

const HEX_KEY = process.env.REACT_APP_DEFINITION_CIPHER_KEY ?? '';

/** Convierte una cadena hex a Uint8Array */
function hexToBuffer(hex) {
    if (!hex || hex.length % 2 !== 0) return new Uint8Array(0);
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < bytes.length; i++) {
        bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    }
    return bytes;
}

/** Convierte una cadena base64 a Uint8Array */
function base64ToBuffer(b64) {
    try {
        const binary = atob(b64);
        const bytes  = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        return bytes;
    } catch {
        return new Uint8Array(0);
    }
}

/** Detecta si el string parece estar cifrado (formato "<b64>.<b64>") */
export function isEncrypted(text) {
    if (typeof text !== 'string') return false;
    const parts = text.split('.');
    return parts.length === 2 && parts[0].length > 0 && parts[1].length > 0;
}

/**
 * Descifra un string AES-256-CBC cifrado por Strapi.
 * Devuelve el HTML plano, o el propio `encrypted` si no se puede descifrar
 * (p. ej. término free que Strapi no cifra, o clave no configurada).
 */
export async function decryptDefinition(encrypted) {
    if (!encrypted || !isEncrypted(encrypted)) return encrypted ?? '';
    if (!HEX_KEY || HEX_KEY.startsWith('REEMPLAZA')) {
        console.warn('[decryptDefinition] REACT_APP_DEFINITION_CIPHER_KEY no configurada');
        return '';
    }

    try {
        const [ivB64, ctB64] = encrypted.split('.');
        const keyMaterial = await crypto.subtle.importKey(
            'raw',
            hexToBuffer(HEX_KEY),
            { name: 'AES-CBC' },
            false,
            ['decrypt']
        );
        const plain = await crypto.subtle.decrypt(
            { name: 'AES-CBC', iv: base64ToBuffer(ivB64) },
            keyMaterial,
            base64ToBuffer(ctB64)
        );
        return new TextDecoder().decode(plain);
    } catch (err) {
        console.error('[decryptDefinition] Error al descifrar:', err);
        return '';
    }
}
